"use client";

import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getCompanyLabelsForSlug,
  normalizeCompanySlug,
} from "../../app/lib/mock-interview/companyMap";
import { resolveJobsDisplayName } from "../../app/lib/resume/jobsCompanyMap";

function storageKey(articleSlug) {
  return `gsr-article-rails:${String(articleSlug || "").trim()}`;
}

function readDismissed(articleSlug) {
  if (typeof window === "undefined") return { mock: false, questions: false };
  try {
    const raw = window.sessionStorage.getItem(storageKey(articleSlug));
    if (!raw) return { mock: false, questions: false };
    const parsed = JSON.parse(raw);
    return {
      mock: parsed?.mock === true,
      questions: parsed?.questions === true,
    };
  } catch {
    return { mock: false, questions: false };
  }
}

function writeDismissed(articleSlug, next) {
  try {
    if (!next.mock && !next.questions) {
      window.sessionStorage.removeItem(storageKey(articleSlug));
      return;
    }
    window.sessionStorage.setItem(storageKey(articleSlug), JSON.stringify(next));
  } catch {
    /* ignore quota / private mode */
  }
}

function firmLabel(firmName, slug) {
  return resolveJobsDisplayName({ companySlug: slug, firmName }) || getCompanyLabelsForSlug(slug)?.[0] || slug.replace(/-/g, " ");
}

function buildHref(path, { articleSlug, utmMedium, utmContent }) {
  const params = new URLSearchParams();
  params.set("utm_source", "article");
  params.set("utm_medium", utmMedium);
  params.set("utm_campaign", String(articleSlug || "article").trim() || "article");
  if (utmContent) params.set("utm_content", utmContent);
  return `${path}?${params.toString()}`;
}

function railPositionClass(side) {
  return side === "left"
    ? "left-[max(0.25rem,calc(50%-28rem-2.75rem))]"
    : "right-[max(0.25rem,calc(50%-28rem-2.75rem))]";
}

/** Vertical label faces the article column (not the viewport edge). */
function verticalTextClass(side) {
  return side === "left"
    ? "[writing-mode:vertical-rl] rotate-180"
    : "[writing-mode:vertical-rl]";
}

function SideRail({ side, title, href, onDismiss }) {
  const isLeft = side === "left";

  return (
    <div
      className={`fixed top-[42%] z-30 hidden -translate-y-1/2 xl:flex flex-col items-center gap-1 ${railPositionClass(side)}`}
    >
      <button
        type="button"
        onClick={onDismiss}
        className={`flex size-5 shrink-0 items-center justify-center rounded-full border border-[#e4e2dd]/80 bg-white/90 text-[10px] leading-none text-[#8a8883] hover:bg-white hover:text-[#27251f] transition-colors ${
          isLeft ? "self-end" : "self-start"
        }`}
        aria-label="Hide sidebar link"
      >
        ×
      </button>
      <Link
        href={href}
        className={`group flex max-h-[min(52vh,22rem)] items-center justify-center border px-2 py-3 text-[11px] font-medium leading-snug tracking-wide text-[#3d5a14] opacity-80 hover:opacity-100 transition-opacity ${verticalTextClass(side)} ${
          isLeft
            ? "rounded-r-md border-[#d6e4bf] bg-[#f4f8ee]/85 hover:bg-[#f4f8ee]/95"
            : "rounded-l-md border-[#e4e2dd] bg-white/85 hover:bg-white/95"
        }`}
      >
        {title}
      </Link>
    </div>
  );
}

function SideRailRestore({ side, label, onRestore }) {
  const isLeft = side === "left";

  return (
    <div
      className={`fixed top-[42%] z-30 hidden -translate-y-1/2 xl:flex ${railPositionClass(side)}`}
    >
      <button
        type="button"
        onClick={onRestore}
        className={`flex items-center justify-center border px-1.5 py-2 text-[10px] font-medium text-[#8a8883] opacity-70 hover:opacity-100 transition-opacity ${verticalTextClass(side)} ${
          isLeft
            ? "rounded-r-md border-[#e4e2dd]/90 bg-white/75 hover:bg-white/90"
            : "rounded-l-md border-[#e4e2dd]/90 bg-white/75 hover:bg-white/90"
        }`}
        aria-label={`Show ${label}`}
      >
        Show
      </button>
    </div>
  );
}

/**
 * Fixed side rails for firm CTAs (xl+ only). Configure per article via `articleSideRails` in JSON.
 * Dismiss is per browser tab (sessionStorage). Use the slim "Show" tab to bring a rail back.
 */
export default function ArticleSideRails({
  articleSlug = "",
  firmName = "",
  mockInterviewSlug = "",
  questionsSlug = "",
}) {
  const mockSlug = normalizeCompanySlug(mockInterviewSlug);
  const questionsSlugNorm = normalizeCompanySlug(questionsSlug);
  const firm = useMemo(
    () => firmLabel(firmName, mockSlug || questionsSlugNorm),
    [firmName, mockSlug, questionsSlugNorm]
  );

  const [dismissed, setDismissed] = useState({ mock: false, questions: false });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.removeItem(storageKey(articleSlug));
    } catch {
      /* migrated to sessionStorage */
    }
    setDismissed(readDismissed(articleSlug));
    setReady(true);
  }, [articleSlug]);

  const persist = useCallback(
    (updater) => {
      setDismissed((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        writeDismissed(articleSlug, next);
        return next;
      });
    },
    [articleSlug]
  );

  const dismissMock = useCallback(() => {
    persist((prev) => ({ ...prev, mock: true }));
  }, [persist]);

  const dismissQuestions = useCallback(() => {
    persist((prev) => ({ ...prev, questions: true }));
  }, [persist]);

  const restoreMock = useCallback(() => {
    persist((prev) => ({ ...prev, mock: false }));
  }, [persist]);

  const restoreQuestions = useCallback(() => {
    persist((prev) => ({ ...prev, questions: false }));
  }, [persist]);

  const restoreAll = useCallback(() => {
    persist({ mock: false, questions: false });
  }, [persist]);

  if (!ready || (!mockSlug && !questionsSlugNorm)) return null;

  const resumePath = mockSlug
    ? `${buildHref("/dashboard/resume", {
        articleSlug,
        utmMedium: "article_rail_resume",
        utmContent: "resume_boost",
      })}&setup=1`
    : null;
  const mockHref = resumePath ? `/?register=1&next=${encodeURIComponent(resumePath)}` : null;

  const questionsHref = questionsSlugNorm
    ? buildHref(`/dashboard/companies/${questionsSlugNorm}/questions`, {
        articleSlug,
        utmMedium: "article_rail_questions",
        utmContent: "interview_questions",
      })
    : null;

  const mockTitle = `ATS resume for ${firm}`;
  const questionsTitle = `Free ${firm} interview questions`;

  const showMock = mockSlug && !dismissed.mock;
  const showQuestions = questionsSlugNorm && !dismissed.questions;
  const showMockRestore = mockSlug && dismissed.mock;
  const showQuestionsRestore = questionsSlugNorm && dismissed.questions;
  const anyDismissed = showMockRestore || showQuestionsRestore;

  if (!showMock && !showQuestions && !anyDismissed) return null;

  return (
    <>
      {showMock ? (
        <SideRail side="left" title={mockTitle} href={mockHref} onDismiss={dismissMock} />
      ) : showMockRestore ? (
        <SideRailRestore side="left" label={mockTitle} onRestore={restoreMock} />
      ) : null}

      {showQuestions ? (
        <SideRail side="right" title={questionsTitle} href={questionsHref} onDismiss={dismissQuestions} />
      ) : showQuestionsRestore ? (
        <SideRailRestore side="right" label={questionsTitle} onRestore={restoreQuestions} />
      ) : null}

      {showMockRestore && showQuestionsRestore ? (
        <button
          type="button"
          onClick={restoreAll}
          className="fixed bottom-6 left-1/2 z-30 hidden -translate-x-1/2 xl:inline-flex items-center rounded-full border border-[#e4e2dd] bg-white/90 px-3 py-1.5 text-[11px] font-medium text-[#6d6c66] opacity-80 hover:opacity-100 shadow-sm transition-opacity"
        >
          Show prep links
        </button>
      ) : null}
    </>
  );
}
